import type { ChatMessageOut, ChatSegmentOut, ChatThreadOut } from "../contract/api";
import type { NotesMessage } from "./result-notes-model";

/**
 * Ask AI (#791), as pure rows: one person's chat about a meeting.
 *
 * The same rows the Rust core's `mf-present` `chat_rows` produces for the apps, from the
 * same shared fixtures (`contract/fixtures/present/chat/`), checked field for field by
 * `engine/present.test.ts`. The server has already turned the model's answer into
 * segments, checked every quote and moment, and filled in today's names; this only groups
 * them for drawing and says what stops a new question. Every sentence is a message key.
 */

/** One piece of an answer: words, or a speaker's own checked words, and its moment. */
export interface ChatPieceRow {
  kind: "text" | "quote";
  text: string;
  /** Who said it, as the meeting names them today. */
  speaker: string | null;
  /** The moment to jump to, in seconds into the meeting. */
  source_start: number | null;
}

/**
 * A run of pieces from the same place: the meeting, or general knowledge related to it,
 * which carries the "Not from the meeting" label.
 */
export interface ChatBlockRow {
  from_meeting: boolean;
  label: NotesMessage | null;
  pieces: ChatPieceRow[];
}

export interface ChatMessageRow {
  id: number;
  role: "user" | "assistant";
  /** The question as asked; empty on an answer. */
  text: string;
  /** The fixed reply, for a question about something other than the meeting. */
  refusal: NotesMessage | null;
  blocks: ChatBlockRow[];
}

export interface ChatRows {
  /** Ask AI is offered for this meeting and this person. */
  enabled: boolean;
  /** A new question can be sent now. */
  can_ask: boolean;
  /** The line that says why not, when something on the server's side stops it. */
  notice: NotesMessage | null;
  /** The chat is full: offer "Start a new chat". */
  can_restart: boolean;
  question_max_chars: number;
  messages: ChatMessageRow[];
}

function message(key: string, params: Record<string, string> = {}): NotesMessage {
  return { key, params, nested: {} };
}

function piece(segment: ChatSegmentOut): ChatPieceRow {
  return {
    kind: segment.kind,
    text: segment.text,
    speaker: segment.speaker ?? null,
    source_start: segment.source_start ?? null,
  };
}

/** Segments grouped into blocks: consecutive pieces from the same place share one. */
export function chatBlocks(segments: readonly ChatSegmentOut[]): ChatBlockRow[] {
  const blocks: ChatBlockRow[] = [];
  for (const segment of segments) {
    const fromMeeting = segment.from_meeting !== false;
    const last = blocks.at(-1);
    if (last && last.from_meeting === fromMeeting) {
      last.pieces.push(piece(segment));
    } else {
      blocks.push({
        from_meeting: fromMeeting,
        label: fromMeeting ? null : message("result.chat_not_from_meeting"),
        pieces: [piece(segment)],
      });
    }
  }
  return blocks;
}

export function chatMessageRow(out: ChatMessageOut): ChatMessageRow {
  return {
    id: out.id,
    role: out.role,
    text: out.text ?? "",
    refusal: out.refusal ? message("result.chat_out_of_scope") : null,
    blocks: out.refusal ? [] : chatBlocks(out.segments ?? []),
  };
}

/** The chat as the panel draws it. */
export function chatRows(thread: ChatThreadOut): ChatRows {
  const enabled = thread.enabled;
  const resting = thread.resting === true;
  const full = thread.full === true;
  let notice: NotesMessage | null = null;
  if (!enabled && thread.unavailable === "meeting_too_long") {
    notice = message("result.chat_too_long_meeting");
  } else if (enabled && resting) {
    notice = message("result.chat_resting");
  } else if (enabled && full) {
    notice = message("result.chat_full");
  }
  return {
    enabled,
    can_ask: enabled && !resting && !full,
    notice,
    can_restart: enabled && full,
    question_max_chars: thread.question_max_chars,
    messages: (thread.messages ?? []).map(chatMessageRow),
  };
}

/**
 * Whether the Ask AI button is shown for this chat: when it is offered, and also when
 * the meeting is too long for it, so the person learns why rather than finding nothing.
 */
export function chatOffered(thread: ChatThreadOut | null): boolean {
  if (!thread) return false;
  return thread.enabled || thread.unavailable === "meeting_too_long";
}

/** The parts of `window.visualViewport` the keyboard math reads. */
export interface VisualViewportLike {
  height: number;
  offsetTop: number;
  scale?: number;
}

/** Where the chat sits while an on-screen keyboard is up: px from the layout viewport's top and bottom. */
export interface KeyboardInset {
  top: number;
  bottom: number;
}

/**
 * Below this, the gap between the layout and visual viewports is browser chrome (a
 * toolbar settling), not a keyboard; every on-screen keyboard is far taller.
 */
export const KEYBOARD_MIN_PX = 60;

/**
 * How much of the layout viewport's bottom the on-screen keyboard covers, so the chat can
 * sit entirely above it: `innerHeight - (visualViewport.height + visualViewport.offsetTop)`.
 * `top` follows the visual viewport when the browser has panned it (iOS does, on focus).
 * Null when there is no keyboard: no visualViewport, a gap under `KEYBOARD_MIN_PX`, or a
 * pinch zoom (scale above 1), which shrinks the visual viewport without any keyboard.
 */
export function keyboardInset(innerHeight: number, viewport: VisualViewportLike | null | undefined): KeyboardInset | null {
  if (!viewport) return null;
  if ((viewport.scale ?? 1) > 1.01) return null;
  const bottom = Math.round(innerHeight - (viewport.height + viewport.offsetTop));
  if (bottom < KEYBOARD_MIN_PX) return null;
  return { top: Math.max(0, Math.round(viewport.offsetTop)), bottom };
}

/** What went wrong with the last request, as the panel words it. */
export type ChatProblem =
  | "load_failed"
  | "rate_limited_soon"
  | "rate_limited_today"
  | "resting"
  | "busy"
  | "full"
  | "too_long"
  | "error"
  | "offline"
  | "delete_failed"
  | "limit_meeting"
  | "fair_use_month";

/** A refused or failed question as one of the panel's problems. */
export function chatProblemOf(err: {
  status?: number;
  code?: string | null;
  retryAfter?: number;
}): ChatProblem {
  // The asker's plan's limits (#874), by code before any status: Pro's month is a 429
  // that is not the daily rate limit, and a meeting's limit is a 409.
  if (err.code === "meeting_limit") return "limit_meeting";
  if (err.code === "fair_use_month") return "fair_use_month";
  if (err.code === "resting") return "resting";
  if (err.code === "rate_limited" || err.status === 429) {
    // A wait of more than an hour is the daily cap; less is the burst limit.
    return (err.retryAfter ?? 0) > 3600 ? "rate_limited_today" : "rate_limited_soon";
  }
  if (err.code === "busy") return "busy";
  if (err.code === "thread_full") return "full";
  if (err.code === "too_long" || err.status === 422) return "too_long";
  return "error";
}

/** The copy key for each problem. */
export const CHAT_PROBLEM_KEY = {
  load_failed: "result.chat_load_failed",
  rate_limited_soon: "result.chat_rate_limited_soon",
  rate_limited_today: "result.chat_rate_limited_today",
  resting: "result.chat_resting",
  busy: "result.chat_busy",
  full: "result.chat_full",
  too_long: "result.chat_too_long",
  error: "result.chat_error",
  offline: "result.chat_offline",
  delete_failed: "result.chat_delete_failed",
  limit_meeting: "result.chat_limit_meeting",
  fair_use_month: "result.chat_fair_use_month",
} as const satisfies Record<ChatProblem, string>;

/** A plan's Ask AI limit as the server reported it (#874): the number, and Pro's reset. */
export interface ChatLimit {
  limit: number | null;
  resetsAt: string | null;
  /** The asker's plan, null without one (#1010); undefined from a server that does not say. */
  plan?: string | null;
}
